import { useState } from 'react';
import { Copy, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface ErrorToastProps {
  title: string;                    // "Error Name (CODE)"
  message: string;                  // User-friendly message
  requestId?: string;               // Request ID for support correlation
  details?: unknown;                // Optional technical details (dev mode)
  onRetry?: () => void;             // Optional retry handler
  className?: string;
}

export function ErrorToast({ 
  title, 
  message, 
  requestId, 
  details, 
  onRetry, 
  className = "" 
}: ErrorToastProps): JSX.Element {
  const [showDetails, setShowDetails] = useState(false);
  const { toast } = useToast();

  const copyRequestId = (): void => {
    if (!requestId) return;

    void navigator.clipboard.writeText(requestId).then(() => {
      toast({
        title: "Copied!",
        description: "Request ID copied to clipboard",
        variant: "default",
      });
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = requestId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      toast({
        title: "Copied!",
        description: "Request ID copied to clipboard",
        variant: "default",
      });
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Error title and message */}
      <div className="space-y-2">
        <div className="font-semibold text-destructive-foreground">
          {title}
        </div>
        <div className="text-sm text-muted-foreground">
          {message}
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        {/* Left side - Request ID */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {requestId && (
            <>
              <span>Request ID:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyRequestId}
                className="h-6 px-2 text-xs font-mono hover:bg-muted"
              >
                <Copy className="w-3 h-3 mr-1" />
                {requestId.slice(0, 8)}...
              </Button>
            </>
          )}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {/* Show details button */}
          {details && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="h-6 px-2 text-xs hover:bg-muted"
            >
              {showDetails ? (
                <>
                  <ChevronDown className="w-3 h-3 mr-1" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronRight className="w-3 h-3 mr-1" />
                  Show details
                </>
              )}
            </Button>
          )}
          
          {/* Retry button */}
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="h-6 px-3 text-xs hover:bg-secondary/10"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Try again
            </Button>
          )}
        </div>
      </div>

      {/* Collapsible details section */}
      {showDetails && details && (
        <div className="pt-3 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">
            Technical Details:
          </div>
          <div className="bg-muted/30 rounded-md p-3 text-xs font-mono overflow-auto max-h-32">
            <pre className="whitespace-pre-wrap break-words">
              {typeof details === 'string' 
                ? details 
                : JSON.stringify(details, null, 2)
              }
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
